/**
 * @module lib/format
 * @description Pure formatting and derived-number helpers shared by every tier.
 */

/** One formatter instance (Intl formatters are costly to construct). */
const USD: Intl.NumberFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
});

/** @returns a whole-dollar USD string, e.g. 1500 → "$1,500". */
export function formatUsd(amount: number): string {
    return USD.format(amount);
}

/** @returns a plain grouped integer, e.g. 1519 → "1,519". */
export function formatCount(value: number): string {
    return value.toLocaleString("en-US");
}

/** Milliseconds in one day. */
const DAY_MS: number = 86_400_000;

/**
 * Days until an appeal closes, derived (never stored) so it stays true.
 * @param endsAt ISO date (yyyy-mm-dd), read as end of that day local time.
 * @param now current time (injectable for tests).
 * @returns whole days left, floored at 0.
 */
export function daysLeft(endsAt: string, now: Date = new Date()): number {
    const end: Date = new Date(`${endsAt}T23:59:59`);
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / DAY_MS));
}

/** @returns percent of goal raised, clamped 0–100, rounded. */
export function percentRaised(raisedUsd: number, goalUsd: number): number {
    if (goalUsd <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((raisedUsd / goalUsd) * 100)));
}

/** @returns "1 cause" / "3 causes" style pluralization. */
export function plural(count: number, singular: string, pluralForm: string = `${singular}s`): string {
    return `${formatCount(count)} ${count === 1 ? singular : pluralForm}`;
}
